/**
 * rim-matrix.js — Rim Compatibility Matrix
 *
 * Two responsibilities:
 *
 * 1. FILTER — checkbox filters with AND-across-groups / OR-within-group logic.
 *    State persisted to sessionStorage so it survives page refreshes.
 *
 * 2. CELL LINKS — each filled <td> in the Liquid has a data-href="" attribute.
 *    When data-href is non-empty, this script wraps the cell's contents in an
 *    <a> tag so the entire cell is clickable, navigating to that product URL.
 *    Cells with an empty data-href remain non-clickable.
 *
 * Vanilla JS, no dependencies, no optional chaining, no nullish coalescing.
 * Safari 14+ compatible.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'rim-matrix-filters';
  var FADE_MS = 220; // must be >= the CSS opacity transition duration

  var section = document.querySelector('.rim-matrix-section');
  if (!section) { return; }

  var checkboxes = section.querySelectorAll('.rim-matrix-section__filter-check');
  var rows = section.querySelectorAll('tr.rim-matrix-section__row[data-tubeless]');
  var emptyRow = section.querySelector('.rim-matrix-section__empty-row');

  /* ------------------------------------------------------------------
     1. CELL LINKS
     Read data-href from each filled cell. If non-empty, wrap its
     contents in an <a> that fills the entire cell via position:absolute.
  ------------------------------------------------------------------ */
  (function buildCellLinks() {
    var r, row, allTds, c, td, href, link;

    for (r = 0; r < rows.length; r++) {
      row = rows[r];
      allTds = row.querySelectorAll('td');

      for (c = 0; c < allTds.length; c++) {
        td = allTds[c];
        if (!td.classList.contains('rim-matrix-section__cell--filled')) { continue; }

        href = td.getAttribute('data-href');
        if (!href) { continue; } // no URL set — leave cell non-clickable

        link = document.createElement('a');
        link.href = href;
        link.className = 'rim-matrix-section__cell-link';

        // Move checkmark span into the <a>
        while (td.firstChild) {
          link.appendChild(td.firstChild);
        }
        td.appendChild(link);
        td.classList.add('rim-matrix-section__cell--has-link');
      }
    }
  }());

  /* ------------------------------------------------------------------
     2. FILTERS
  ------------------------------------------------------------------ */

  if (!checkboxes.length) { return; }

  /* Restore saved state */
  var saved = null;
  try {
    var raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) { saved = JSON.parse(raw); }
  } catch (e) {}

  if (saved) {
    var i, cb, key;
    for (i = 0; i < checkboxes.length; i++) {
      cb = checkboxes[i];
      key = cb.getAttribute('data-group') + '|' + cb.getAttribute('data-value');
      if (typeof saved[key] !== 'undefined') {
        cb.checked = saved[key];
      }
    }
  }

  function getCheckedValues(group) {
    var values = [];
    var i, cb;
    for (i = 0; i < checkboxes.length; i++) {
      cb = checkboxes[i];
      if (cb.getAttribute('data-group') === group && cb.checked) {
        values.push(cb.getAttribute('data-value'));
      }
    }
    return values;
  }

  function inArray(needle, haystack) {
    var i;
    for (i = 0; i < haystack.length; i++) {
      if (haystack[i] === needle) { return true; }
    }
    return false;
  }

  /* For space-separated multi-value attributes (e.g. data-discipline="mtb fat") */
  function anyMatch(attrValue, checkedValues) {
    var parts = attrValue ? attrValue.split(' ') : [];
    var i, j;
    for (i = 0; i < parts.length; i++) {
      for (j = 0; j < checkedValues.length; j++) {
        if (parts[i] === checkedValues[j]) { return true; }
      }
    }
    return false;
  }

  /* Row show/hide with opacity fade + deferred layout collapse */
  var hideTimers = {};

  function showRow(row) {
    var idx = row.getAttribute('data-row-index');
    if (hideTimers[idx]) {
      clearTimeout(hideTimers[idx]);
      delete hideTimers[idx];
    }
    row.style.display = '';
    void row.offsetHeight; // force reflow so transition fires
    row.classList.remove('rim-matrix-section__row--hidden');
  }

  function hideRow(row) {
    var idx = row.getAttribute('data-row-index');
    row.classList.add('rim-matrix-section__row--hidden');
    hideTimers[idx] = setTimeout(function () {
      if (row.classList.contains('rim-matrix-section__row--hidden')) {
        row.style.display = 'none';
      }
      delete hideTimers[idx];
    }, FADE_MS);
  }

  function saveFilterState() {
    var state = {};
    var i, cb, k;
    for (i = 0; i < checkboxes.length; i++) {
      cb = checkboxes[i];
      k = cb.getAttribute('data-group') + '|' + cb.getAttribute('data-value');
      state[k] = cb.checked;
    }
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch (e) {}
  }

  function applyFilters() {
    var tubelessVals    = getCheckedValues('tubeless');
    var sidewallVals    = getCheckedValues('sidewall');
    var strengthVals    = getCheckedValues('strength');
    var disciplineVals  = getCheckedValues('discipline');
    var visibleCount = 0;
    var i, row, pass;

    for (i = 0; i < rows.length; i++) {
      row = rows[i];
      pass = (
        inArray(row.getAttribute('data-tubeless'),   tubelessVals) &&
        inArray(row.getAttribute('data-sidewall'),   sidewallVals) &&
        inArray(row.getAttribute('data-strength'),   strengthVals) &&
        anyMatch(row.getAttribute('data-discipline'), disciplineVals)
      );
      if (pass) { showRow(row); visibleCount++; }
      else       { hideRow(row); }
    }

    if (emptyRow) {
      if (visibleCount === 0) {
        emptyRow.classList.add('rim-matrix-section__empty-row--visible');
      } else {
        emptyRow.classList.remove('rim-matrix-section__empty-row--visible');
      }
    }

    saveFilterState();
  }

  /* Stamp each row with an index for the timer map */
  var r;
  for (r = 0; r < rows.length; r++) {
    rows[r].setAttribute('data-row-index', String(r));
  }

  var c;
  for (c = 0; c < checkboxes.length; c++) {
    checkboxes[c].addEventListener('change', applyFilters);
  }

  applyFilters(); // apply any restored sessionStorage state on load

}());
